import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { ProfileEntity } from '@interpals/api-web';

import { makeProfilePhoto, updateAccount, updateAccountAbout, updateUserMatchSettings } from 'redux/account/actions';
import { deleteAlbumPhoto, moderationDeletePhoto, uploadPhotoToAlbumNew } from 'redux/photo-albums/actions';

import {
    fetchProfile,
    blockUser,
    unblockUser,
    clearProfile,
    createBookmark,
    deleteBookmark,
    updateRelation,
    updateUserStatus,
} from './actions';

export interface ProfileState {
    data?: ProfileEntity;
}

const initialState: ProfileState = {};

const profileSlice = createSlice({
    name: 'profile',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchProfile.fulfilled, (state, { payload }) => {
            state.data = payload;
        });
        builder.addCase(blockUser.fulfilled, (state, { payload }) => {
            if (state.data) {
                state.data.relation = payload;
            }
        });
        builder.addCase(unblockUser.fulfilled, (state, { payload }) => {
            if (state.data) {
                state.data.relation = payload;
            }
        });
        builder.addCase(createBookmark.fulfilled, (state) => {
            if (state.data) {
                state.data.is_bookmarked = true;
            }
        });
        builder.addCase(deleteBookmark.fulfilled, (state) => {
            if (state.data) {
                state.data.is_bookmarked = false;
            }
        });
        builder.addCase(updateRelation.fulfilled, (state, { payload }) => {
            if (state.data) {
                state.data.relation = payload;
            }
        });
        builder.addCase(updateUserStatus.fulfilled, (state, { payload }) => {
            if (state.data) {
                state.data.status_message = payload.message;
                state.data.status_message_updated = payload.updated;
            }
        });
        builder.addCase(uploadPhotoToAlbumNew.fulfilled, (state, { payload }) => {
            const avatarsPhotoIds = state.data?.profile_photos.album.photo_ids;

            if (payload && state.data?.profile_photos.album.id === payload?.aid) {
                if (!state.data.avatar) {
                    state.data.avatar = payload;
                }

                state.data.profile_photos.album.photo_ids = avatarsPhotoIds
                    ? [...avatarsPhotoIds, payload.id]
                    : [payload.id];
                state.data.profile_photos.avatars = [...state.data.profile_photos.avatars, payload];
            }
        });
        builder.addCase(makeProfilePhoto.fulfilled, (state, { payload }) => {
            if (state.data && payload?.cover && state.data.id === payload.uid) {
                state.data.avatar = payload.cover;
                state.data.profile_photos.album = payload;
            }
        });
        builder.addCase(clearProfile, (state) => {
            state.data = undefined;
        });
        // Moderator can update a profile, so these profile change should take effect here
        builder.addCase(updateAccount.fulfilled, (state, { payload: account }) => {
            if (state.data?.id === account.id) {
                state.data = account;
            }
        });
        builder.addCase(updateAccountAbout.fulfilled, (state, { payload: account }) => {
            if (state.data?.id === account.id) {
                state.data = account;
            }
        });
        builder.addCase(updateUserMatchSettings.fulfilled, (state, { payload: matchSettings }) => {
            if (state.data?.id === matchSettings.uid) {
                state.data.match_settings = matchSettings;
            }
        });
        builder.addMatcher(isAnyOf(deleteAlbumPhoto.fulfilled, moderationDeletePhoto), (state, { payload }) => {
            if (payload && state.data?.profile_photos.album.id === payload.albumId) {
                state.data.profile_photos.avatars = state.data.profile_photos.avatars.filter(
                    (photo) => photo.id !== payload.photoId,
                );
            }
        });
    },
});

export default profileSlice.reducer;
